import * as _ from 'lodash'
import { EMPTY, Observable, throwError } from 'rxjs'
import { catchError, filter, map, pluck, switchMap } from 'rxjs/operators'

import { Container, Item, ContainerStatus, SearchResult, Wemi, SearchQuery } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { API_ENDPOINTS, fetch } from '@skinsoft/network'
import { CONTAINER_TYPES_FOR_SEARCH } from '@skinsoft/data-store'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import {  ItemDataStore } from '../item/item-data-store'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'

declare const ITEM_SEARCH_TYPES

export class ContainerDataStore extends EntityDataStore<Container> {
    public static getInstance() {
        if (!ContainerDataStore.instance) {
            ContainerDataStore.instance = new ContainerDataStore()
        }

        return ContainerDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$containerDataStore = ContainerDataStore.getInstance()
    }

    private static instance: ContainerDataStore = null as any

    private static fragmentParser(response) {
        return response._embedded
            ? (response._embedded['numeric-container-items'] || []).concat(
                  response._embedded['physical-container-items'] || [],
              )
            : []
    }

    constructor() {
        super(
            'physical-container-items',
            CONTAINER_TYPES_FOR_SEARCH,
            undefined,
            undefined,
            undefined,
            ContainerDataStore.fragmentParser,
        )
    }

    public fetchAll(options): Observable<Container[]> {
        return super.fetchAll({ endpoint: '/container-items', ...options })
    }

    public addItem(container: Container, itemId: Item['id']) {
        return ItemDataStore.getInstance()
            .fetchOne(itemId)
            .pipe(
                map((item) => {
                    const actualContainers =
                        _.get(item, 'conditioningItemSchema.actualContainer', []) || []
                    actualContainers.push({
                        container: container.id,
                        date: getCurrentDateAsSkinDate(),
                        precision: null
                    })
                    _.set(item, 'conditioningItemSchema.actualContainer', actualContainers)
                    return item
                }),
                switchMap((item) => ItemDataStore.getInstance().update(item.id, item)),
                filter(_.identity),
            )
    }

    public removeItem(containerId: string, item: Item) {
        _.set(
            item,
            'conditioningItemSchema.actualContainer',
            _.get(item, 'conditioningItemSchema.actualContainer').filter((container) =>  {
                if(container && container.container) {
                    return container.container != containerId
                }
                return false // logger (id item, id contenant, field 'conditioningItemSchema.actualContainer', field value) (this is not normal that container.container is not set)
            }),
        )
        return ItemDataStore.getInstance().update(item.id, item).pipe(filter(_.identity))
    }

    public getAssociatedItems(containerId: string, searchTypes: string[] = ITEM_SEARCH_TYPES): Observable<(SearchResult|Wemi)[]> {
        
        return ItemDataStore.getInstance()
            .searchAll(
                {
                    pageSize: 50,
                    types: searchTypes,
                    page: 0,
                    advancedSearches: [
                        {
                            leftCriteria: {
                                entityField: '65c647789a58d61b96d9fb00',
                                operator: 'equals',
                                type: 'criteria',
                                value: {
                                    completeSearch: false,
                                    refId: containerId,
                                    type: 'ref',
                                },
                            },
                            linkOperator: null,
                            rightCriteria: null,
                            type: 'requestSearch',
                        },
                    ],
                }
            )
            .pipe(
                pluck('allData'),
            )
    }

    /**
     * 
     * @param searchText 
     * @param pageSize 
     * @param page 
     * @param statusList 
     * @param orderingFields 
     * @param excludedIds 
     * @param includeIdSubResults When searchText value is an id (mongoDb), setting to true will include the entities referencing the id in addition to the entity corresponding to the id
     * @returns 
     */
    public searchAmongStatusList(
        searchText: string,
        pageSize: number,
        page: number,
        statusList: ContainerStatus[],
        orderingFields?: { entityFields: string[], sortAsc: boolean },
        excludedIds?: string[],
        includeIdSubResults: boolean = true
    ) {
        const searchQuery: SearchQuery = {
            queryText: searchText,
            sortObject: orderingFields,
            page: page,
            pageSize: pageSize,
            types: CONTAINER_TYPES_FOR_SEARCH,
            excludedIds: excludedIds,
            advancedSearches: [
                statusList
                    .map((apiStatus) => ({
                        leftCriteria: {
                            entityField: '5baccdcb70aa490001802634',
                            operator: apiStatus,
                            type: 'criteria',
                        },
                        linkOperator: 'OR',
                        rightCriteria: null as any,
                        type: 'requestSearch',
                    }))
                    .reduce((fullQuery, queryPart) => ({ ...queryPart, rightCriteria: fullQuery })),
            ],
        }

        // Omit<SearchQuery, 'advancedSearches'> (ts version >= 2.8)
        return this.searchAll(
            searchQuery,
            undefined,
            undefined,
            undefined,
            undefined,
            includeIdSubResults,
        )
    }

    public cascadeLocationOnContent(containerId: string, containerEndPoint?: string) {
        let endpointRoot = API_ENDPOINTS.physicalContainerItem
        if(containerEndPoint && containerEndPoint === SEARCH_TYPES.numericContainerItem) {
            endpointRoot = API_ENDPOINTS.numericContainerItem
        }

        return this.proxy.fetch(`${endpointRoot}/setLocationContent?entityId=${containerId}`, undefined, 'POST').pipe(
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        )
    }
}
